'use strict'

// Relative import — this file IS @symbo-ls/workspace-shared, and an absolute
// self-specifier serializes into the pushed library payload as an
// unresolvable deep-subpath dependency (private scope; the public importmap
// only maps bare package roots — tickets/execution-board.md §1).
import { PERSONA_ROLE_CATALOG, personaRoleLabel, personaActive } from '../functions/persona.js'

// PersonaPill / PersonaPicker — tickets/fable.md PERSONA-6: the "you are
// viewing as X" exit affordance + role picker.
//
// ── Premise check (2026-08-12) ─────────────────────────────────────────
// Built against the STATE CONTRACT tickets/opus.md PERSONA-1/2 and
// tickets/sonnet.md PERSONA-4/5 have DOCUMENTED but not yet LANDED
// (re-verified before writing this file: zero `_personaSession` reads or
// writes anywhere in server/, sdk/, or workspace/; zero
// `sdk.startPersona`/`endPersona`/`getPersona` on the live SDK; PERSONA-1/2/3
// in opus.md and PERSONA-4/5 in sonnet.md are all still headed `## OPEN`).
// Both components below render INERT/INVISIBLE until that state exists —
// `hide:` gates on `personaActive(s.root)`, which reads `s.root._personaSession`
// and is always `undefined` (falsy) today, so landing this file changes
// NOTHING for any current session or route. The moment PERSONA-5 starts
// writing `_personaSession`, these components come alive with no further
// wiring beyond mounting `PersonaPill: {}` at a shell root (see the mount
// note at the bottom of this file).
//
// ── Design decisions (full reasoning recorded in tickets/fable.md
// PERSONA-6) ────────────────────────────────────────────────────────────
// 1. Fixed BOTTOM-LEFT corner pill, `display:'contents'` wrapper. Proven
//    necessary by a REAL bug in the closest precedent, DemoClaimBanner.js:
//    a shell-root sibling with a real box becomes a STATIC child of the
//    shell's single `1fr` grid row and pushes the router's `content` into a
//    collapsed implicit row — every non-home route rendered blank. This file
//    follows the exact same `display:'contents'` wrapper + `position:'fixed'`
//    descendant fix. `hide:` not `if:` — reserved-but-invisible, zero shift
//    (DESIGN_LANGUAGE §8.4, Rule 66).
// 2. Calm, not alarm — reuses `CanvasHelpCard` (the SAME glassy pill
//    DemoClaimBanner already uses in this exact corner), `title`/`caption`
//    ink, no status hue. "Wash + neutral ink" (DESIGN_LANGUAGE §8.5 rule 3).
//    Corner-collision note: DemoClaimBanner (isDemo-only) and PersonaPill
//    (persona-active-only) both want bottom-left — they can never actually
//    co-render because `personaActive` can only ever be true for a REAL
//    admin session, and demo sessions are already locked out of admin-only
//    surfaces by the existing DEMO_BLOCKED_ROUTE_RE convention (app.js) —
//    PROVIDED whoever builds the "start a persona" entry point also gates it
//    on `!root.isDemo`. Flagged for PERSONA-1/2/4, not re-solved here.
// 3. ROLE only, never a person — the pill shows "Viewing as <Role>", full
//    stop. No avatar, no name, ever.
// 4. Exit lives IN the pill (mounted at the shell root, sibling of routed
//    content, never inside AppHeader/nav chrome) so "reachable everywhere
//    the pill is" holds even for a role whose OWN chrome fails to render.
// 5. Disabled-for-this-persona controls get ATTRIBUTION, not extra visual
//    weight — see functions/persona.js `personaDisabledProps()`: a real
//    `disabled` + a token-driven `:disabled` fill/ink step, never `opacity`.
//
// ── Correction vs. the 2026-08-11 draft (recorded, not silently changed) ──
// The prior session's decisions #2 and #4 (this same ticket, same day)
// described a "specific individual" selection mode — avatar+name on the
// pill when "the admin explicitly selected a specific individual"; a
// person-search in the picker reusing `ContextDropdownItem`'s avatar slot.
// The SAME ticket's HARD INVARIANT — repeated verbatim on every PERSONA-1..6
// entry, dated the same day, framed as a security invariant with a MANDATED
// regression test — says "there is no user id in this feature at all" and
// the separate "SCOPE DECIDED" entry explicitly deletes "the per-member
// picker" from the design ("there is only ever ONE identity, the admin's").
// A picker or pill state that names a specific individual is exactly the
// shape the invariant calls "wrong by construction". Built ROLE-ONLY here.
// A person-name LOOKUP could still be added later as a pure client-side
// filter over these same 3 rows (typing "Natia" surfaces "Natia — Team
// member" as a sub-label; selecting it still only ever calls
// `startPersonaRole('member')`, never anything carrying her id) — not built
// today because nothing needs it yet and the smaller surface is the safer
// default until Nika confirms it's wanted.
//
// ── Reuse (3-tier check) ────────────────────────────────────────────────
// `CanvasHelpCard` (→ `CanvasMessage`, `canvas-card-glass` theme) for the
// bar chrome — the exact component DemoClaimBanner already uses in this
// corner. `MenuPanel` / `MenuPanelItem` for the picker — the ticket's own
// top-level guidance, and DESIGN_LANGUAGE §7.7 ("already canonical, now
// exclusive"). Icons from the existing brand set (`eye`, `chevronUp/Down`)
// — nothing new drawn. Exit stays TEXT-only (no icon) per decision #3's own
// "text-forward, not photo-forward" reasoning applied consistently — the
// one icon on the pill (`eye`) already carries the "viewing as" concept.

// PersonaPicker — the role list. Independently mountable (bare key
// `PersonaPicker: {}` works from any future admin entry point, e.g. an
// AppHeader admin menu or a settings page — WHERE that entry point lives is
// an open product question, not resolved here, see the ticket) — PersonaPill
// below also composes it inline so an already-active persona can switch
// directly to a different role without exiting first.
export const PersonaPicker = {
  extends: 'MenuPanel',
  position: 'absolute',
  bottom: '100%',
  left: '0',
  marginBottom: 'Z',
  // minWidth inherits MenuPanel's own 'E1' — 3 short rows don't need a
  // bespoke width.
  zIndex: '1',
  role: 'menu',
  ariaLabel: (el) => el.call('polyglot', 'members.persona.viewAs'),
  hide: (el, s) => !(s.root && s.root._personaPickerOpen),
  subscribeTo: ['_personaPickerOpen'],

  // Escape closes + returns focus to the trigger — standard disclosure
  // pattern, same idiom ContextDropdownItem uses for its own submenu.
  onKeydown: (e, el, s) => {
    if (e.key !== 'Escape') return
    e.preventDefault()
    e.stopPropagation()
    s.root.update({ _personaPickerOpen: false })
    const trigger = el.parent && el.parent.RoleToggle
    if (trigger && trigger.node && typeof trigger.node.focus === 'function') {
      trigger.node.focus()
    }
  },

  // Focus the first row on the CLOSED→OPEN edge only (never steal focus on
  // every re-render) — same false→true edge-detection idiom as app.js's
  // `_prevAssistantOpen`, scoped to this element instance instead of a
  // module-level mutable (Rule 33).
  scope: {
    focusFirstOnOpen(el, s) {
      const isOpen = !!(s.root && s.root._personaPickerOpen)
      if (isOpen && !el._personaPickerWasOpen && el.node) {
        const first = el.node.querySelector('[role="menuitem"]')
        if (first) first.focus()
      }
      el._personaPickerWasOpen = isOpen
    }
  },
  onRender: (el, s) => el.scope.focusFirstOnOpen(el, s),
  onUpdate: (el, s) => el.scope.focusFirstOnOpen(el, s),

  Label: {
    tag: 'p',
    padding: 'Y Z1',
    margin: '0',
    fontSize: 'Y',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    color: 'caption',
    text: '{{ members.persona.viewAs | polyglot }}'
  },

  // Data rows (role/label pairs), not DOMQL prop objects — `childrenAs:
  // 'state'` so each row's own data lands on its child `s` alongside the
  // normal state plumbing (s.root, s.key, ...), exactly like ContextDropdown's
  // `Menu.children` + `ContextDropdownItem`.
  children: (el, s) => PERSONA_ROLE_CATALOG,
  childrenAs: 'state',
  childExtends: 'MenuPanelItem',
  childProps: {
    role: 'menuitem',
    text: (el, s) =>
      el
        .call('polyglot', 'members.persona.viewAsRole')
        .replace('{role}', el.call('polyglot', (s && s.label) || '')),
    onClick: (e, el, s) => {
      const root = s && s.root
      if (!root) return
      root.update({ _personaPickerOpen: false })
      el.call('startPersonaRole', s.role)
    }
  }
}

export const PersonaPill = {
  tag: 'div',
  // Critical — see decision #1 above. No box of its own; every visible
  // child is `position:'fixed'`, so this wrapper never enters the shell
  // grid's `1fr` content row.
  display: 'contents',
  subscribeTo: ['_personaSession'],
  hide: (el, s) => !personaActive(s.root),

  // BOOT-FROM-TRUTH (tickets/opus.md PERSONA-3: "reload must restore AS
  // persona, not silently drop back to admin"). One oracle call at mount:
  // `refreshPersonaSession` asks the SERVER what the presented token's
  // persona state is (GET /persona, the persona-LENIENT worker route) and
  // makes `_personaSession` agree — a reloaded tab re-renders the pill from
  // server truth even though DOMQL state was lost with the page, and a
  // stale claim (ended remotely / expired) is shed instead of trapping the
  // admin behind a banner the server no longer honors. Fire-and-forget:
  // the function never rejects, fails SAFE (state untouched) on transport
  // errors, and no-ops on a pre-oracle @symbo.ls/sdk.
  onRender: (el) => {
    // A no-op when the boot already asked (loadPrivateData, priv:start).
    el.call('refreshPersonaSession', { mount: true })
  },

  // Invisible click-outside catcher for the picker — same purely-functional
  // idiom as ContextDropdown's own Backdrop (no background/visual styling,
  // `position:'fixed'` so it covers the WHOLE viewport, not just this
  // wrapper's own subtree). zIndex sits one below the bar so the bar's own
  // buttons always win the hit-test.
  PickerBackdrop: {
    // FA515 — `s.root && s.root.X` short-circuits, so a run where `s.root`
    // hasn't resolved yet never touches `.X` and the tracker never learns
    // this factory depends on it. Explicit `hideDeps` forces the re-run.
    hide: (el, s) => !(s.root && s.root._personaPickerOpen),
    hideDeps: ['_personaPickerOpen'],
    position: 'fixed',
    inset: '0',
    // One below the bar (see the card's TOP-LAYER PROMOTION note).
    zIndex: '10399',
    onPointerdown: (e, el, s) => s.root.update({ _personaPickerOpen: false })
  },

  // The bar itself. Named to match its base per the auto-extend convention
  // (same as DemoClaimBanner's own `CanvasHelpCard:` child key — no
  // `extends:` needed, the key IS the extend).
  CanvasHelpCard: {
    position: 'fixed',
    right: 'auto',
    left: 'A',
    bottom: 'A',
    // TOP-LAYER PROMOTION (PERSONA-3: "a persona with fewer permissions must
    // not be able to hide its own exit button" — including via a blocking
    // overlay it cannot dismiss). The shell's documented ladder
    // (CreditBlocker.js): DropdownRoot 10010 → context menu 10100 →
    // CreditBlocker 10300 → ModalRoot 10500 → AiBridge/Voice 10600. The
    // pill's old 1500 sat UNDER the whole blocking band — a workspace
    // hitting the credit wall mid-persona stranded the admin with the exit
    // painted beneath the gate. 10400 clears every persistent blocker while
    // staying below ModalRoot: modals are chrome the admin opened and can
    // always dismiss, so painting over their backdrop would be noise, not
    // safety. NB nothing in this app uses the NATIVE top layer
    // (dialog.showModal / popover) today — if that ever changes, this must
    // move to popover='manual' promotion, because no z-index beats the top
    // layer.
    zIndex: '10400',
    flow: 'x',
    align: 'center',
    gap: 'Z1',
    padding: 'Z A',
    boxShadow: 'none',
    maxWidth: 'calc(100dvw - var(--spacing-B))',
    '@mobileL': {
      display: 'flex',
      left: 'Z',
      right: 'Z',
      // Live-verified defect (Chrome MCP / puppeteer, both themes, PERSONA-6
      // taste pass 2026-08-13): a flush `bottom: 'Z'` here put the pill
      // directly ON TOP of the AI composer bar at phone widths — both are
      // `position:'fixed'` and bottom-docked, so the exit affordance visibly
      // covered "Whats up?" instead of sitting above it. Same
      // `--slot-bottom` (+`--chip-row` for the phone suggestion-chip band)
      // clearance already wired app-wide for exactly this class of
      // bottom-docked overlay — `CanvasScaffold.js:212` and
      // `tickets/pages/board.js:960` use the identical
      // `calc(var(--slot-bottom) + var(--chip-row))` expression to dock
      // above the SAME composer. Reused verbatim rather than re-deriving a
      // second measurement. NB `DemoClaimBanner.js` (the pill shape this
      // component's own header cites as its precedent) still has the
      // pre-existing flush-`bottom:'Z'` version of this bug — flagged
      // separately, not fixed here (out of this ticket's scope).
      bottom: 'calc(var(--slot-bottom) + var(--chip-row))',
      maxWidth: 'none'
    },
    // Not a help tip — no decorative swipe animation here.
    Lottie: null,

    Icon: {
      // "ROLE LENS" (opus.md PERSONA-1's own phrase) — `eye` is the direct
      // visual match.
      icon: 'eye',
      boxSize: 'Z2',
      minWidth: 'Z2',
      color: 'caption',
      ariaHidden: 'true'
    },

    RoleToggle: {
      tag: 'button',
      type: 'button',
      flow: 'x',
      align: 'center',
      gap: 'X',
      padding: '0',
      background: 'transparent',
      borderWidth: '0',
      cursor: 'pointer',
      color: 'title',
      fontSize: 'Z2',
      fontWeight: '700',
      whiteSpace: 'nowrap',
      ariaHaspopup: 'true',
      // FA515 — same short-circuit trap as PickerBackdrop.hide above; every
      // conditional `s.root && s.root.X` read below gets an explicit `Deps`.
      ariaExpanded: (el, s) => (s.root && s.root._personaPickerOpen ? 'true' : 'false'),
      ariaExpandedDeps: ['_personaPickerOpen'],
      Label: {
        tag: 'span',
        // The SHIPPED SDK contract is `{ role }` (sdk 26402c58, wrapper
        // 5b917d25) — this read previously spoke the retired `{ type, id }`
        // draft shape, so a real session labeled itself "a different
        // persona". Tolerate the legacy shape defensively (personaRoleLabel
        // already falls back for unknown roles — same fail-open stance as
        // personaActive).
        text: (el, s) => {
          const sess = s.root && s.root._personaSession
          const role = sess && (sess.role || (sess.type === 'role' ? sess.id : null))
          // The role LABEL comes from PERSONA_ROLE_CATALOG, which already
          // carries bundle keys; `personaRoleLabel` is the English fallback
          // for an enum value the catalog has no row for (editor/member).
          const row = PERSONA_ROLE_CATALOG.find((r) => r.role === role)
          return el
            .call('polyglot', 'members.persona.viewAsRole')
            .replace('{role}', el.call('polyglot', (row && row.label) || personaRoleLabel(role)))
        },
        textDeps: ['_personaSession']
      },
      Chevron: {
        icon: (el, s) => (s.root && s.root._personaPickerOpen ? 'chevronDown' : 'chevronUp'),
        iconDeps: ['_personaPickerOpen'],
        boxSize: 'Y',
        color: 'caption',
        ariaHidden: 'true'
      },
      onClick: (e, el, s) => {
        s.root.update({ _personaPickerOpen: !(s.root && s.root._personaPickerOpen) })
      }
    },

    ExitBtn: {
      tag: 'button',
      type: 'button',
      minHeight: 'controlH',
      minWidth: 'F',
      padding: '0 Z1',
      round: 'radiusPill',
      theme: 'tertiary',
      fontSize: 'Y2',
      fontWeight: '700',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      transition: '160ms ease background',
      ':hover': { background: 'hover' },
      // Token-driven fill/ink step, never opacity — decision #5 +
      // DESIGN_LANGUAGE §8.5 rule 6 + the shipped `dd83fc31` precedent.
      ':disabled': {
        background: 'overlay',
        color: 'caption',
        cursor: 'not-allowed'
      },
      // FA515 — same short-circuit trap; explicit Deps on both conditional
      // reads below.
      disabled: (el, s) => !!(s.root && s.root._personaExiting),
      disabledDeps: ['_personaExiting'],
      // "Exiting…" swap stays inside the button's own reserved geometry
      // (DESIGN_LANGUAGE §8.4 mechanism 4) — minWidth floors it so the swap
      // never visibly resizes the pill.
      text: (el, s) =>
        el.call(
          'polyglot',
          s.root && s.root._personaExiting ? 'members.persona.exiting' : 'members.persona.exit'
        ),
      textDeps: ['_personaExiting'],
      onClick: async (e, el, s) => {
        if (s.root && s.root._personaExiting) return
        s.root.update({ _personaExiting: true, _personaPickerOpen: false })
        const ok = await el.call('endPersonaSession')
        if (!ok) {
          // EXIT RECONCILIATION (PERSONA-3). ok:false means the end call
          // could not confirm the session died — but "could not confirm"
          // covers the write landing and the RESPONSE being lost. Ask the
          // oracle: it classifies the presented claim through the server's
          // own resolver and sheds blob + state here if the session is in
          // fact dead; if it is genuinely still live (or the oracle is
          // unreachable too) state stays put and the pill keeps telling the
          // truth. A SUCCESSFUL end needs no oracle round trip — the shed
          // in endPersonaSession already matches acknowledged server state.
          await el.call('refreshPersonaSession')
        }
        s.root.update({ _personaExiting: false })
        if (!ok && s.root && s.root._personaSession) {
          console.warn(
            '[PersonaPill] exit failed — persona session may still be active server-side'
          )
        }
      }
    },

    PersonaPicker: {}
  }
}

// ── Mount ────────────────────────────────────────────────────────────────
// Bare key `PersonaPill: {}` at a shell root, as a SIBLING of routed
// `content` — never inside AppHeader or any nav chrome (decision #4: a role
// whose chrome fails to render must not take the exit with it). Today wired
// at `workspace/packages/workspace/app.js` only (the concrete "every route"
// the ticket's acceptance criteria means). Other shell roots (canvas
// app.js, marketplace app.js, ...) can pick it up with the same one-line
// addition once persona is confirmed to apply there too — the component
// itself needs no per-app change, it already no-ops with no active persona.
